import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import {
  CarOutlined,
  CompassOutlined,
  DownOutlined,
  FallOutlined,
  FireOutlined,
  ReloadOutlined,
  RiseOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  UpOutlined,
  WalletOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { get_Dashboard } from "../../api/dashboard";

const { RangePicker } = DatePicker;

// Suffixe monétaire affiché après les montants (ex: "Ar", "€")
const DEVISE = "";

// ─────────────────────────── Palette ───────────────────────────
const C = {
  blue: "#4f6ef7",
  green: "#10b981",
  purple: "#8b5cf6",
  orange: "#f59e0b",
  teal: "#06b6d4",
  red: "#ef4444",
};
const G = {
  blue: ["#4f6ef7", "#2f54eb"],
  green: ["#10b981", "#059669"],
  purple: ["#8b5cf6", "#6d28d9"],
  orange: ["#f59e0b", "#ea580c"],
  teal: ["#06b6d4", "#0e7490"],
  red: ["#ef4444", "#b91c1c"],
};

// ─────────────────────────── Styles ───────────────────────────
const CSS = `
  .dp-root { scrollbar-width: thin; }

  /* Bannière */
  .dp-banner {
    position: sticky; top: 0; z-index: 20;
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
    padding: 12px 20px; margin-bottom: 16px; border-radius: 14px; color: #fff;
    background: linear-gradient(135deg, #011528 0%, #0b3a6b 60%, #1e6bd6 100%);
    box-shadow: 0 6px 18px rgba(1, 21, 40, .25);
  }
  .dp-banner-title { display: flex; align-items: center; gap: 12px; }
  .dp-banner-icon {
    width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,.15);
    display: flex; align-items: center; justify-content: center; font-size: 20px;
  }
  .dp-banner h2 { margin: 0; color: #fff; font-size: 18px; font-weight: 700; line-height: 1.2; }
  .dp-banner small { color: rgba(255,255,255,.7); font-size: 12px; }

  /* Cartes KPI */
  .dp-kpi {
    position: relative; overflow: hidden; height: 100%;
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; border-radius: 14px; color: #fff;
    box-shadow: 0 4px 14px rgba(20, 40, 100, .18);
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .dp-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(20, 40, 100, .25); }
  .dp-kpi::after {
    content: ""; position: absolute; right: -26px; top: -26px;
    width: 96px; height: 96px; border-radius: 50%; background: rgba(255,255,255,.12);
  }
  .dp-kpi-icon {
    width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0;
    background: rgba(255,255,255,.22); font-size: 22px;
    display: flex; align-items: center; justify-content: center;
  }
  .dp-kpi-body { min-width: 0; position: relative; z-index: 1; }
  .dp-kpi-title { font-size: 12px; font-weight: 500; opacity: .9; }
  .dp-kpi-value { font-size: 24px; font-weight: 700; line-height: 1.25; white-space: nowrap; }
  .dp-kpi-value small { font-size: 12px; font-weight: 500; margin-left: 4px; opacity: .9; }
  .dp-kpi-sub {
    font-size: 11.5px; opacity: .9; margin-top: 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  /* Indicateurs complémentaires */
  .dp-chip { border-radius: 10px; padding: 10px 12px; border: 1px solid; height: 100%; }
  .dp-chip-label { font-size: 11.5px; font-weight: 600; }
  .dp-chip-value { font-size: 17px; font-weight: 700; color: #1a2035; margin-top: 2px; }

  /* Cartes / graphiques */
  .dp-card { border-radius: 14px !important; box-shadow: 0 2px 10px rgba(20, 40, 100, .08); height: 100%; }
  .dp-card .ant-card-head { min-height: 46px; border-bottom: 1px solid #eef1f8; }
  .dp-card .ant-card-head-title { font-weight: 700; color: #1a2035; }
  .dp-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 8px; }
  .dp-stack { display: flex; height: 16px; border-radius: 99px; overflow: hidden; background: #eef1f8; }
  .dp-stack > span { display: block; height: 100%; }
  .dp-bar { height: 8px; border-radius: 99px; background: #eef1f8; overflow: hidden; }
  .dp-bar > span { display: block; height: 100%; border-radius: 99px; }
  .dp-bar-sm { height: 5px; margin-top: 4px; }
  .dp-bar-head { display: flex; justify-content: space-between; gap: 8px; font-size: 13px; margin-bottom: 4px; }
  .dp-bar-head > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  /* Onglets */
  .dp-tabs .ant-tabs-tab { font-weight: 600; font-size: 14px; }
  .dp-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { color: #4f6ef7; }
  .dp-tabs .ant-tabs-ink-bar { background: #4f6ef7; height: 3px; }

  /* Tableaux */
  .dp-table .ant-table { font-size: 14px; }
  .dp-table .ant-table-thead > tr > th {
    background: #eef4ff; color: #1d3a6d; font-weight: 700; white-space: nowrap;
  }
  .dp-table .ant-table-tbody > tr > td { padding: 12px 14px; }
  .dp-table .dp-row-alt > td { background: #f8fbff; }
  .dp-table .dp-row-neg > td { background: #fff1f0; }
  .dp-table .ant-table-tbody > tr.ant-table-row:hover > td { background: #dbeafe !important; }
`;

// ─────────────────────────── Helpers ───────────────────────────
const nf = (v, d = 0) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "-"
    : Number(v).toLocaleString("fr-FR", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });

const money = (v) => `${nf(v)} ${DEVISE}`.trim();
const dateFr = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");
const axe = (r) => `${r.region_depart ?? "?"} → ${r.region_arrivee ?? "?"}`;

// Hauteur du corps de tableau : remplit l'espace restant du conteneur de Home
const calcTableY = () =>
  Math.max(280, Math.round(window.innerHeight * 0.8) - 48 - 240);

const numCol = (title, key, d = 0, extra = {}) => ({
  title,
  dataIndex: key,
  key,
  align: "right",
  sorter: (a, b) => (a[key] ?? 0) - (b[key] ?? 0),
  render: (v) => nf(v, d),
  ...extra,
});

// Valeur + mini barre proportionnelle
const barRender =
  (max, color, d = 0) =>
    (v) =>
    (
      <div style={{ minWidth: 96 }}>
        <div style={{ textAlign: "right" }}>{nf(v, d)}</div>
        <div className="dp-bar dp-bar-sm">
          <span
            style={{
              width: `${max ? (Math.max(0, v || 0) / max) * 100 : 0}%`,
              background: color,
            }}
          />
        </div>
      </div>
    );

// Conso : vert si <= moyenne de la flotte, orange sinon
const consoRender = (avg) => (v) =>
  v === null || v === undefined ? (
    "-"
  ) : (
    <span
      style={{
        color: avg && v > avg ? "#d97706" : "#059669",
        fontWeight: 600,
      }}
    >
      {nf(v, 2)}
    </span>
  );

const margeRender = (v) => (
  <span style={{ color: v < 0 ? "#dc2626" : "#059669", fontWeight: 600 }}>
    {nf(v)}
  </span>
);

const MargeTag = ({ v }) =>
  v === null || v === undefined ? (
    "-"
  ) : (
    <Tag color={v >= 0 ? "green" : "red"}>{nf(v, 1)} %</Tag>
  );

const EcartTag = ({ v, enCours }) => {
  if (enCours) return <Tag>En cours</Tag>;
  if (v === null || v === undefined) return "-";
  return (
    <Tag color={v > 0 ? "red" : "green"}>
      {v > 0 ? "+" : ""}
      {nf(v, 1)} %
    </Tag>
  );
};

// ─────────────────────────── Composants ───────────────────────────
const KpiCard = ({ icon, palette, title, value, suffix, sub }) => (
  <div
    className="dp-kpi"
    style={{ background: `linear-gradient(135deg, ${palette[0]}, ${palette[1]})` }}
  >
    <div className="dp-kpi-icon">{icon}</div>
    <div className="dp-kpi-body">
      <div className="dp-kpi-title">{title}</div>
      <div className="dp-kpi-value">
        {value}
        {suffix ? <small>{suffix}</small> : null}
      </div>
      {sub ? <div className="dp-kpi-sub">{sub}</div> : null}
    </div>
  </div>
);

// Barres horizontales (top clients, axes, classement camions)
const TopBars = ({ items = [], label, value, fmt, color, max }) => {
  if (!items.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const top = max ?? Math.max(0, ...items.map(value));
  return (
    <>
      {items.map((it, i) => (
        <div key={i} style={{ marginBottom: 14 }}>
          <div className="dp-bar-head">
            <span>{label(it)}</span>
            <strong>{fmt ? fmt(it) : money(value(it))}</strong>
          </div>
          <div className="dp-bar">
            <span
              style={{
                width: `${top ? (Math.max(0, value(it)) / top) * 100 : 0}%`,
                background: color,
              }}
            />
          </div>
        </div>
      ))}
    </>
  );
};

const CardTitle = ({ color, children }) => (
  <span>
    <i className="dp-dot" style={{ background: color }} />
    {children}
  </span>
);

// ─────────────────────────── Page ───────────────────────────
const DashboardPerformance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState([moment().startOf("month"), moment()]);
  const [camionId, setCamionId] = useState(null);
  const [camionOptions, setCamionOptions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("synthese");
  const [showMore, setShowMore] = useState(false);
  const [maj, setMaj] = useState(null);
  const [tableY, setTableY] = useState(calcTableY);

  useEffect(() => {
    const onResize = () => setTableY(calcTableY());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 🔹 Chargement
  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await get_Dashboard({
        camion_id: camionId ?? undefined,
        start_date: range[0].format("YYYY-MM-DD"),
        end_date: range[1].format("YYYY-MM-DD"),
      });
      if (res.status === "success") {
        setData(res.data);
        setMaj(moment());
        // Garde la liste des camions vus (le filtre camion réduit par_camion à 1 ligne)
        setCamionOptions((prev) => {
          const map = new Map(prev.map((o) => [o.value, o]));
          (res.data.par_camion || []).forEach((c) =>
            map.set(c.camion_id, {
              value: c.camion_id,
              label: c.camion_reference,
            })
          );
          return [...map.values()];
        });
      } else {
        message.error(res.description || "Erreur de chargement du dashboard");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.description || "Erreur de chargement du dashboard"
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, camionId]);

  const kpis = data?.kpis;
  const classement = data?.classement_camions;
  const avgConso = kpis?.conso_moyenne_100km;

  // 🔹 Filtrage du tableau des factures
  const facturesFiltrees = useMemo(() => {
    const list = data?.factures || [];
    const q = searchText.trim().toLowerCase();
    if (!q) return list;
    return list.filter((f) =>
      [
        f.ref_facture,
        f.client_nom,
        f.ref_devis,
        f.camions,
        f.region_depart,
        f.region_arrivee,
      ].some((x) => x && x.toString().toLowerCase().includes(q))
    );
  }, [data, searchText]);

  const maxOf = (list, key) => Math.max(0, ...(list || []).map((r) => r[key] || 0));

  // 🔹 Cartes principales
  const margeOk = (kpis?.marge_apres_gasoil ?? 0) >= 0;
  const depassement = (kpis?.taux_depassement_gasoil_pct ?? 0) > 0;

  const hero = kpis
    ? [
      {
        icon: <WalletOutlined />,
        palette: G.blue,
        title: "Chiffre d'affaires HT",
        value: nf(kpis.chiffre_affaires_ht),
        suffix: DEVISE,
        sub: `${nf(kpis.nb_factures)} facture(s)`,
      },
      {
        icon: margeOk ? <RiseOutlined /> : <FallOutlined />,
        palette: margeOk ? G.green : G.red,
        title: "Marge après gasoil",
        value: nf(kpis.marge_apres_gasoil),
        suffix: DEVISE,
        sub: `${nf(kpis.marge_pct, 1)} % du CA`,
      },
      {
        icon: <CompassOutlined />,
        palette: G.purple,
        title: "Distance parcourue",
        value: nf(kpis.distance_totale_km),
        suffix: "km",
        sub: `${nf(kpis.nb_trajets)} trajets · ${nf(kpis.nb_camions_actifs)} camion(s)`,
      },
      {
        icon: <FireOutlined />,
        palette: G.orange,
        title: "Gasoil consommé",
        value: nf(kpis.gasoil_consomme_total),
        suffix: "L",
        sub: `Coût : ${money(kpis.cout_gasoil_total)}`,
      },
      {
        icon: <ThunderboltOutlined />,
        palette: G.teal,
        title: "Conso moyenne",
        value: nf(kpis.conso_moyenne_100km, 2),
        suffix: "L/100 km",
        sub: `${nf(kpis.vitesse_moyenne_kmh, 1)} km/h en moyenne`,
      },
      {
        icon: <WarningOutlined />,
        palette: depassement ? G.red : G.green,
        title: "Dépassements gasoil",
        value: nf(kpis.taux_depassement_gasoil_pct, 1),
        suffix: "%",
        sub: `${nf(kpis.nb_factures_depassement_gasoil)} facture(s) > devis`,
      },
    ]
    : [];

  // 🔹 Indicateurs complémentaires
  const ecartGasoil = kpis?.ecart_gasoil_total ?? 0;
  const chips = kpis
    ? [
      { t: "Tonnage", v: `${nf(kpis.tonnage_total, 1)} t`, c: C.blue },
      { t: "Tonne-km", v: `${nf(kpis.tonne_km_total)} t·km`, c: C.blue },
      { t: "Conso / tonne-km", v: `${nf(kpis.conso_moyenne_tonne_km, 4)} L`, c: C.orange },
      {
        t: "Écart gasoil vs devis",
        v: `${ecartGasoil > 0 ? "+" : ""}${nf(ecartGasoil, 1)} L`,
        c: ecartGasoil > 0 ? C.red : C.green,
      },
      { t: "Part du gasoil dans le CA", v: `${nf(kpis.part_gasoil_ca_pct, 1)} %`, c: C.orange },
      { t: "CA par km", v: money(kpis.ca_par_km), c: C.green },
      { t: "CA TTC", v: money(kpis.chiffre_affaires_ttc), c: C.green },
      {
        t: "Trajets en cours",
        v: nf(kpis.nb_trajets_en_cours),
        c: kpis.nb_trajets_en_cours > 0 ? C.orange : C.green,
      },
    ]
    : [];

  // 🔹 Colonnes : factures
  // const colonnesFactures = [
  //   {
  //     title: "Facture",
  //     dataIndex: "ref_facture",
  //     key: "ref_facture",
  //     fixed: "left",
  //     render: (v) => <strong style={{ color: "#1d3a6d" }}>{v}</strong>,
  //     sorter: (a, b) => (a.ref_facture || "").localeCompare(b.ref_facture || ""),
  //   },
  //   {
  //     title: "Date",
  //     dataIndex: "date_facture",
  //     key: "date_facture",
  //     sorter: (a, b) => (a.date_facture || "").localeCompare(b.date_facture || ""),
  //     render: dateFr,
  //   },
  //   { title: "Client", dataIndex: "client_nom", key: "client_nom" },
  //   { title: "Axe", key: "axe", render: (_, r) => axe(r) },
  //   { title: "Camions", dataIndex: "camions", key: "camions" },
  //   numCol("Tonnage (t)", "tonnage", 2),
  //   numCol("Distance (km)", "distance_totale_km"),
  //   numCol("Gasoil (L)", "gasoil_consomme_total", 1),
  //   numCol("Gasoil prévu (L)", "gasoil_prevu", 1),
  //   {
  //     title: "Écart gasoil",
  //     key: "ecart_gasoil_pct",
  //     align: "center",
  //     sorter: (a, b) => (a.ecart_gasoil_pct ?? 0) - (b.ecart_gasoil_pct ?? 0),
  //     render: (_, r) => (
  //       <EcartTag v={r.ecart_gasoil_pct} enCours={r.nb_trajets_en_cours > 0} />
  //     ),
  //   },
  //   numCol("L/100 km", "conso_par_100km", 2, { render: consoRender(avgConso) }),
  //   numCol(`CA HT ${DEVISE}`.trim(), "total_ht", 0, {
  //     render: barRender(maxOf(data?.factures, "total_ht"), C.blue),
  //   }),
  //   numCol(`Coût gasoil ${DEVISE}`.trim(), "cout_gasoil_reel"),
  //   numCol(`Marge ${DEVISE}`.trim(), "marge_apres_gasoil", 0, { render: margeRender }),
  //   {
  //     title: "Marge %",
  //     key: "marge_pct",
  //     align: "center",
  //     sorter: (a, b) => (a.marge_pct ?? 0) - (b.marge_pct ?? 0),
  //     render: (_, r) => <MargeTag v={r.marge_pct} />,
  //   },
  //   {
  //     title: "Alertes",
  //     key: "alertes",
  //     render: (_, r) => (
  //       <Space size={4}>
  //         {r.nb_trajets_en_cours > 0 && (
  //           <Tag color="orange">{r.nb_trajets_en_cours} en cours</Tag>
  //         )}
  //         {r.nb_trajets_anomalies > 0 && (
  //           <Tag color="red">{r.nb_trajets_anomalies} anomalie(s)</Tag>
  //         )}
  //       </Space>
  //     ),
  //   },
  // ];
  const colonnesFactures = [
    {
      title: "Facture",
      dataIndex: "ref_facture",
      key: "ref_facture",
      fixed: "left",
      width: 130,
      render: (v) => <strong style={{ color: "#1d3a6d" }}>{v}</strong>,
      sorter: (a, b) => (a.ref_facture || "").localeCompare(b.ref_facture || ""),
    },
    {
      title: "Date",
      dataIndex: "date_facture",
      key: "date_facture",
      width: 110,
      sorter: (a, b) => (a.date_facture || "").localeCompare(b.date_facture || ""),
      render: dateFr,
    },
    { title: "Client", dataIndex: "client_nom", key: "client_nom", width: 180, ellipsis: true },
    { title: "Axe", key: "axe", width: 160, ellipsis: true, render: (_, r) => axe(r) },
    { title: "Camions", dataIndex: "camions", key: "camions", width: 100 },
    numCol("Tonnage (t)", "tonnage", 2, { width: 120 }),
    numCol("Distance (km)", "distance_totale_km", undefined, { width: 140 }),
    numCol("Gasoil (L)", "gasoil_consomme_total", 1, { width: 130 }),
    numCol("Gasoil prévu (L)", "gasoil_prevu", 1, { width: 150 }),
    {
      title: "Écart gasoil",
      key: "ecart_gasoil_pct",
      align: "center",
      width: 120,
      sorter: (a, b) => (a.ecart_gasoil_pct ?? 0) - (b.ecart_gasoil_pct ?? 0),
      render: (_, r) => (
        <EcartTag v={r.ecart_gasoil_pct} enCours={r.nb_trajets_en_cours > 0} />
      ),
    },
    numCol("L/100 km", "conso_par_100km", 2, { width: 120, render: consoRender(avgConso) }),
    numCol(`CA HT ${DEVISE}`.trim(), "total_ht", 0, {
      width: 140,
      render: barRender(maxOf(data?.factures, "total_ht"), C.blue),
    }),
    numCol(`Coût gasoil ${DEVISE}`.trim(), "cout_gasoil_reel", undefined, { width: 150 }),
    numCol(`Marge ${DEVISE}`.trim(), "marge_apres_gasoil", 0, { width: 130, render: margeRender }),
    {
      title: "Marge %",
      key: "marge_pct",
      align: "center",
      width: 110,
      sorter: (a, b) => (a.marge_pct ?? 0) - (b.marge_pct ?? 0),
      render: (_, r) => <MargeTag v={r.marge_pct} />,
    },
    {
      title: "Alertes",
      key: "alertes",
      width: 180,
      render: (_, r) => (
        <Space size={4}>
          {r.nb_trajets_en_cours > 0 && (
            <Tag color="orange">{r.nb_trajets_en_cours} en cours</Tag>
          )}
          {r.nb_trajets_anomalies > 0 && (
            <Tag color="red">{r.nb_trajets_anomalies} anomalie(s)</Tag>
          )}
        </Space>
      ),
    },
  ];

  // 🔹 Colonnes : camions
  // const colonnesCamions = [
  //   {
  //     title: "Camion",
  //     dataIndex: "camion_reference",
  //     key: "camion_reference",
  //     fixed: "left",
  //     render: (v) => <strong style={{ color: "#1d3a6d" }}>{v}</strong>,
  //     sorter: (a, b) =>
  //       (a.camion_reference || "").localeCompare(b.camion_reference || ""),
  //   },
  //   numCol("Trajets", "nb_trajets"),
  //   numCol("Factures", "nb_factures"),
  //   numCol("Distance (km)", "distance_totale_km", 0, {
  //     render: barRender(maxOf(data?.par_camion, "distance_totale_km"), C.purple),
  //   }),
  //   numCol("Gasoil consommé (L)", "gasoil_consomme_total", 1),
  //   numCol("Gasoil approvisionné (L)", "gasoil_approvisionne", 1),
  //   numCol("L/100 km", "conso_par_100km", 2, { render: consoRender(avgConso) }),
  //   numCol("Heures de roulage", "heures_roulage", 1),
  //   numCol("Vitesse moy. (km/h)", "vitesse_moyenne_kmh", 1),
  //   {
  //     title: "En cours",
  //     dataIndex: "nb_trajets_en_cours",
  //     key: "nb_trajets_en_cours",
  //     align: "center",
  //     render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : 0),
  //   },
  // ];
  const colonnesCamions = [
    {
      title: "Camion",
      dataIndex: "camion_reference",
      key: "camion_reference",
      fixed: "left",
      width: 130,
      render: (v) => <strong style={{ color: "#1d3a6d" }}>{v}</strong>,
      sorter: (a, b) =>
        (a.camion_reference || "").localeCompare(b.camion_reference || ""),
    },
    numCol("Trajets", "nb_trajets", undefined, { width: 100 }),
    numCol("Factures", "nb_factures", undefined, { width: 100 }),
    numCol("Distance (km)", "distance_totale_km", 0, {
      width: 160,
      render: barRender(maxOf(data?.par_camion, "distance_totale_km"), C.purple),
    }),
    numCol("Gasoil consommé (L)", "gasoil_consomme_total", 1, { width: 170 }),
    numCol("Gasoil approvisionné (L)", "gasoil_approvisionne", 1, { width: 190 }),
    numCol("L/100 km", "conso_par_100km", 2, { width: 120, render: consoRender(avgConso) }),
    numCol("Heures de roulage", "heures_roulage", 1, { width: 160 }),
    numCol("Vitesse moy. (km/h)", "vitesse_moyenne_kmh", 1, { width: 170 }),
    {
      title: "En cours",
      dataIndex: "nb_trajets_en_cours",
      key: "nb_trajets_en_cours",
      align: "center",
      width: 100,
      render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : 0),
    },
  ];

  // 🔹 Colonnes : clients / axes (mêmes indicateurs agrégés)
  const colonnesAgregees = (colonnesLibelle, list) => [
    ...colonnesLibelle,
    numCol("Factures", "nb_factures"),
    numCol("Tonnage (t)", "tonnage", 2),
    numCol("Distance (km)", "distance_totale_km"),
    numCol("Gasoil (L)", "gasoil_consomme_total", 1),
    numCol("L/100 km", "conso_par_100km", 2, { render: consoRender(avgConso) }),
    numCol(`CA HT ${DEVISE}`.trim(), "total_ht", 0, {
      render: barRender(maxOf(list, "total_ht"), C.blue),
    }),
    numCol(`Marge ${DEVISE}`.trim(), "marge_apres_gasoil", 0, { render: margeRender }),
    {
      title: "Marge %",
      key: "marge_pct",
      align: "center",
      sorter: (a, b) => (a.marge_pct ?? 0) - (b.marge_pct ?? 0),
      render: (_, r) => <MargeTag v={r.marge_pct} />,
    },
  ];

  const colonnesClients = colonnesAgregees(
    [
      {
        title: "Client",
        dataIndex: "client_nom",
        key: "client_nom",
        fixed: "left",
        render: (v) => <strong style={{ color: "#1d3a6d" }}>{v ?? "—"}</strong>,
      },
    ],
    data?.par_client
  );

  const colonnesAxes = colonnesAgregees(
    [
      {
        title: "Axe",
        key: "axe",
        fixed: "left",
        render: (_, r) => <strong style={{ color: "#1d3a6d" }}>{axe(r)}</strong>,
      },
    ],
    data?.par_axe
  );

  // 🔹 Props communes des tableaux
  const tableProps = {
    className: "dp-table",
    size: "middle",
    loading,
    scroll: { x: "max-content", y: tableY },
    pagination: {
      defaultPageSize: 20,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100],
      showTotal: (t) => `${t} ligne(s)`,
      position: ["bottomCenter"],
    },
    rowClassName: (r, i) =>
      `${i % 2 ? "dp-row-alt" : ""} ${r.marge_apres_gasoil < 0 ? "dp-row-neg" : ""}`,
  };

  const maxConso = Math.max(
    0,
    ...[...(classement?.economes || []), ...(classement?.gourmands || [])].map(
      (c) => c.conso_par_100km || 0
    )
  );

  const tabLabel = (txt, n) => (
    <span>
      {txt} {n !== undefined && <Tag color="blue">{n}</Tag>}
    </span>
  );

  const tabs = [
    {
      key: "synthese",
      label: tabLabel("Synthèse"),
      children: kpis ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <Card className="dp-card" size="small" title={<CardTitle color={C.blue}>Répartition du CA</CardTitle>}>
                <div style={{ fontSize: 13, color: "#6b7a99", marginBottom: 10 }}>
                  Sur {money(kpis.chiffre_affaires_ht)} de CA HT
                </div>
                <div className="dp-stack">
                  <span
                    style={{
                      width: `${Math.min(100, Math.max(0, kpis.part_gasoil_ca_pct ?? 0))}%`,
                      background: C.orange,
                    }}
                  />
                  <span
                    style={{
                      width: `${Math.max(
                        0,
                        Math.min(100 - (kpis.part_gasoil_ca_pct ?? 0), kpis.marge_pct ?? 0)
                      )}%`,
                      background: C.green,
                    }}
                  />
                </div>
                <div style={{ marginTop: 16 }}>
                  <div className="dp-bar-head">
                    <span>
                      <i className="dp-dot" style={{ background: C.orange }} />
                      Coût gasoil
                    </span>
                    <strong>
                      {money(kpis.cout_gasoil_total)} · {nf(kpis.part_gasoil_ca_pct, 1)} %
                    </strong>
                  </div>
                  <div className="dp-bar-head">
                    <span>
                      <i className="dp-dot" style={{ background: margeOk ? C.green : C.red }} />
                      Marge après gasoil
                    </span>
                    <strong style={{ color: margeOk ? "#059669" : "#dc2626" }}>
                      {money(kpis.marge_apres_gasoil)} · {nf(kpis.marge_pct, 1)} %
                    </strong>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card className="dp-card" size="small" title={<CardTitle color={C.purple}>Top clients (CA HT)</CardTitle>}>
                <TopBars
                  items={(data.par_client || []).slice(0, 5)}
                  label={(c) => c.client_nom ?? "—"}
                  value={(c) => c.total_ht}
                  color={C.purple}
                />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card className="dp-card" size="small" title={<CardTitle color={C.teal}>Top axes (CA HT)</CardTitle>}>
                <TopBars
                  items={(data.par_axe || []).slice(0, 5)}
                  label={axe}
                  value={(a) => a.total_ht}
                  color={C.teal}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card className="dp-card" size="small" title={<CardTitle color={C.green}>Camions les plus économes</CardTitle>}>
                <TopBars
                  items={classement?.economes}
                  label={(c) => c.camion_reference}
                  value={(c) => c.conso_par_100km}
                  fmt={(c) => `${nf(c.conso_par_100km, 2)} L/100 km · ${nf(c.distance_totale_km)} km`}
                  color={C.green}
                  max={maxConso}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="dp-card" size="small" title={<CardTitle color={C.red}>Camions les plus gourmands</CardTitle>}>
                <TopBars
                  items={classement?.gourmands}
                  label={(c) => c.camion_reference}
                  value={(c) => c.conso_par_100km}
                  fmt={(c) => `${nf(c.conso_par_100km, 2)} L/100 km · ${nf(c.distance_totale_km)} km`}
                  color={C.red}
                  max={maxConso}
                />
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <Empty />
      ),
    },
    {
      key: "factures",
      label: tabLabel("Factures", data?.factures?.length ?? 0),
      children: (
        <Table
          {...tableProps}
          dataSource={facturesFiltrees}
          columns={colonnesFactures}
          rowKey={(r) => `${r.facture_id}-${r.client_id ?? 0}`}
        />
      ),
    },
    {
      key: "camions",
      label: tabLabel("Camions", data?.par_camion?.length ?? 0),
      children: (
        <Table
          {...tableProps}
          dataSource={data?.par_camion || []}
          columns={colonnesCamions}
          rowKey="camion_id"
        />
      ),
    },
    {
      key: "clients",
      label: tabLabel("Clients", data?.par_client?.length ?? 0),
      children: (
        <Table
          {...tableProps}
          dataSource={data?.par_client || []}
          columns={colonnesClients}
          rowKey={(r) => r.client_id ?? "sans-client"}
        />
      ),
    },
    {
      key: "axes",
      label: tabLabel("Axes", data?.par_axe?.length ?? 0),
      children: (
        <Table
          {...tableProps}
          dataSource={data?.par_axe || []}
          columns={colonnesAxes}
          rowKey={(r) => `${r.region_depart}-${r.region_arrivee}`}
        />
      ),
    },
  ];

  // Raccourcis du RangePicker (prop `ranges`, compatible antd v4 / moment)
  const ranges = {
    "Ce mois": [moment().startOf("month"), moment()],
    "Mois dernier": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
    "30 derniers jours": [moment().subtract(29, "days"), moment()],
    "Cette année": [moment().startOf("year"), moment()],
  };

  return (
    <div
      className="dp-root"
      style={{ height: "calc(80vh - 48px)", overflowY: "auto", paddingRight: 4 }}
    >
      <style>{CSS}</style>

      {/* 🔹 Bannière + filtres (reste visible au scroll) */}
      <div className="dp-banner">
        <div className="dp-banner-title">
          <div className="dp-banner-icon">
            <CarOutlined />
          </div>
          <div>
            <h2>Dashboard performance</h2>
            <small>
              Du {range[0].format("DD/MM/YYYY")} au {range[1].format("DD/MM/YYYY")}
              {maj ? ` · mis à jour à ${maj.format("HH:mm")}` : ""}
            </small>
          </div>
        </div>
        <Space wrap>
          <RangePicker
            value={range}
            ranges={ranges}
            format="DD/MM/YYYY"
            allowClear={false}
            onChange={(v) => v && setRange(v)}
          />
          <Select
            allowClear
            showSearch
            placeholder="Tous les camions"
            style={{ width: 200 }}
            options={camionOptions}
            value={camionId}
            onChange={(v) => setCamionId(v ?? null)}
            optionFilterProp="label"
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchDashboard}
            loading={loading}
            style={{ backgroundColor: "#4f6ef7", borderColor: "#4f6ef7" }}
          >
            Rafraîchir
          </Button>
        </Space>
      </div>

      <Spin spinning={loading && !data}>
        {/* 🔹 Alertes qualité de données */}
        {kpis && (kpis.nb_trajets_en_cours > 0 || kpis.nb_trajets_anomalies > 0) && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16, borderRadius: 12 }}
            message={`${kpis.nb_trajets_en_cours} trajet(s) sans arrivée enregistrée, ${kpis.nb_trajets_anomalies} trajet(s) avec kilométrage ou carburant incohérent`}
            description="Les distances et consommations des factures concernées sont partielles ou incohérentes ; les factures avec trajet en cours sont exclues des écarts de gasoil."
          />
        )}

        {/* 🔹 Cartes principales */}
        <Row gutter={[16, 16]}>
          {hero.map((h) => (
            <Col key={h.title} xs={24} sm={12} md={8} xl={4}>
              <KpiCard {...h} />
            </Col>
          ))}
        </Row>

        {/* 🔹 Indicateurs complémentaires (repliables) */}
        {kpis && (
          <div style={{ margin: "12px 0 4px" }}>
            <Button
              type="link"
              size="small"
              onClick={() => setShowMore((s) => !s)}
              icon={showMore ? <UpOutlined /> : <DownOutlined />}
              style={{ paddingLeft: 0 }}
            >
              {showMore ? "Masquer" : "Afficher"} les indicateurs complémentaires
            </Button>
            {showMore && (
              <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                {chips.map((c) => (
                  <Col key={c.t} xs={12} md={6} xl={3}>
                    <div
                      className="dp-chip"
                      style={{ background: `${c.c}14`, borderColor: `${c.c}40` }}
                    >
                      <div className="dp-chip-label" style={{ color: c.c }}>
                        {c.t}
                      </div>
                      <div className="dp-chip-value">{c.v}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        )}

        {/* 🔹 Onglets */}
        <Tabs
          className="dp-tabs"
          style={{ marginTop: 8 }}
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
          tabBarExtraContent={
            activeTab === "factures" ? (
              <Input
                allowClear
                placeholder="Rechercher (facture, client, camion, axe...)"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ width: 320 }}
              />
            ) : null
          }
        />
      </Spin>
    </div>
  );
};

export default DashboardPerformance;